// English (US) translation file.

export default {
  common: {
    save: 'Save',
    close: 'Close',
    search: 'Search',
    back: 'Back',
    cancel: 'Cancel',
    continue: 'Continue',
    submit: 'Submit',
    confirm: 'Confirm',
    delete: 'Delete',
    save_changes: 'Save changes',
    loading: 'Loading...',
  },
  pages: {
    account: {
      tabs: {
        dashboard: 'Dashboard',
        fleet_overview: 'Fleet overview',
        unit_overview: 'All units',
        campaigns: 'Campaigns',
        members: 'Members',
      },
      dashboard: {
        active_fault_codes: {
          title: 'Active Fault Codes',
          units_title: 'Units with active fault codes',
        },
        connectivity: {
          title: 'Connectivity',
          units_title: 'Connectivity status',
        },
        fuel_consumption: {
          title: 'Fuel Consumption',
          details_title: 'Fuel Consumption Details',
        },
      },
      fleet_overview: {
        pinned_fleets: 'Pinned fleets',
        other_fleets: 'Other fleets',
        pin: 'Pin fleet',
        unpin: 'Unpin fleet',
        edit: 'Edit fleet',
        remove: 'Remove fleet',
      },
      unit_overview: {
        title: 'Units',
        unverified_ownership: 'Unverified ownership',
        add_to_fleet: 'Add to fleet',
      },
      campaigns: {
        active_table: {
          title: 'Active campaigns',
          download_campaigns: 'Download campaigns (xlsx)',
          empty_state: {
            title: 'There are no active campaigns for your units right now.',
          },
        },
        performed_table: {
          title: 'Performed campaigns',
          empty_state: {
            title: 'There are no performed campaigns for your units.',
          },
        },
      },
      members: {
        title: 'Members',
        remove_members_from_account: 'Remove {{selectedRows}} members from account',
      },
    },
    fleet: {
      tabs: {
        dashboard: 'Dashboard',
        unit_overview: 'All units',
        campaigns: 'Campaigns',
      },
      campaigns: {
        title: 'Active campaigns',
        download_campaigns: 'Download campaigns (xlsx)',
        empty_state: {
          title: 'You have no active campaigns for this fleet',
        },
      },
    },
    unit: {
      tabs: {
        specifications: 'Specifications',
        campaigns: 'Campaigns',
        coverage: 'Warranty & Coverage',
        maintenance_history: 'Maintenance history',
        manuals_handbooks: 'Manuals & Handbooks',
        diagnostics_log: 'Diagnostics log',
        services: 'Digital Services',
        settings: 'Settings',
      },
      spare_parts: 'Spare parts',
      manuals_selection_description: 'Which product do you want to find manuals and handbooks for.',
      specifications: {
        title: 'Specifications',
      },
      campaigns: {
        active_table: {
          title: 'Active campaigns',
          empty_state: {
            title: 'There are no active campaigns for this unit right now.',
          },
        },
        performed_table: {
          title: 'Performed campaigns',
          empty_state: {
            title: 'There are no performed campaigns for this unit.',
          },
        },
      },
      coverage: {
        cta: {
          heading: 'Information about warranty & coverage',
          message: 'For questions regarding warranty & coverage contact your Volvo Penta or OEM dealer.',
          find_dealer: 'Find your nearest dealer',
          learn_more: 'Learn more',
        },
        empty_state: {
          title: 'No warranty and/or coverage was found for this unit',
        },
      },
      publications: {
        select_menu: {
          category: 'Category',
          designation: 'Designation',
          product_type: 'Product type',
          language: 'Language',
        },
        empty_state: {
          title: 'No publications available for this unit',
        },
      },
      services: {
        title: 'Available Services',
        all_services: {
          title: 'Activate all services',
        },
        list: {
          ActiveMonitoring: {
            Marine: {
              title: 'Extended',
              description:
                'Get all the benefits of Core plus automated email alerts sent directly to your selected dealer.',
              checklist: [
                'Alert your dealer instantly if your system detects a critical fault code',
                'Enable your dealer to prepare immediate corrective actions',
              ],
            },
            Industrial: {
              title: 'Active monitoring',
              description:
                'Active Monitoring helps customers keep their Volvo Penta products running by detecting issues early, supporting faster service, and giving clear insights into product health and usage.\n\nActive Monitoring also enables Remote Software Updates, a service for installing software campaigns at your convenience over the air.',
              checklist: [
                'Higher uptime through early warnings and proactive support',
                'Faster, better-prepared service with the right information available in advance',
              ],
            },
          },
          ActiveMonitoringCore: {
            Marine: {
              title: 'Core',
              description:
                'Share basic vehicle data with your dealer to get faster support during maintenance or unexpected issues.',
              checklist: [
                'Let your dealer monitor product health and track operating hours',
                'Allow your dealer to analyze fault codes instantly for proactive service planning',
              ],
            },
            Industrial: {
              title: 'Connected Services Core',
              description:
                'Core lets the Volvo Penta dealer monitor the Volvo Penta product. <ActiveMonitoringCore>For further details, see the Service Description for Connected Services Core.</ActiveMonitoringCore>',
              checklist: [],
            },
          },
          ActiveMonitoringEcWifi: {
            Marine: {
              title: 'Extended',
              description:
                'Enable your dealer to track vehicle data and receive instant alerts on critical fault codes to prepare immediate corrective actions.',
              checklist: [
                'Alert your dealer instantly if your system detects a critical fault code',
                'Enable your dealer to prepare immediate corrective actions',
              ],
            },
            Industrial: {
              title: 'Extended',
              description:
                'Enable your dealer to track vehicle data and receive instant alerts on critical fault codes to prepare immediate corrective actions.',
              checklist: [
                'Alert your dealer instantly if your system detects a critical fault code',
                'Enable your dealer to prepare immediate corrective actions',
              ],
            },
          },
          Penta_Position: {
            Marine: {
              title: 'Location services',
              description:
                'Location Services helps customers quickly locate and manage their connected Volvo Penta products through a map view in Volvo Penta Connect, making service management and fleet oversight easier.',
              checklist: [
                'Improved fleet visibility with a map showing the latest location of connected units',
                'Faster service response through easy access to unit location, including operating hours and fault codes',
              ],
            },
            Industrial: {
              title: 'Location services',
              description:
                'Location Services helps customers quickly locate and manage their connected Volvo Penta products through a map view in Volvo Penta Connect, making service management and fleet oversight easier.',
              checklist: [
                'Improved fleet visibility with a map showing the latest location of connected units',
                'Faster service response through easy access to unit location, including operating hours and fault codes',
              ],
            },
          },
        },
        request_sent: 'Request sent',
        empty_state: {
          title: 'Service provisioning not available',
          message: 'Digital service provisioning is not available for this unit.',
        },
        active_services_title: 'Active services',
        available_services_title: 'Available services',
        activate_button: 'Activate service',
        terminate_button: 'Terminate',
        read_more_button: 'Read more',
        requires_prefix: 'Requires {{serviceName}}',
      },
      settings: {
        unit_settings: {
          title: 'Unit settings',
          unit_name: 'Unit name',
          home_port: 'Home port',
          operating_location: 'Operating location',
          upload_image: 'Upload image',
          add_files: 'Add files',
          helperText: 'Png, .jpg file of max 3mb',
        },
        network_settings: {
          title: 'Network settings',
          empty_state: {
            title: 'No active networks available',
            message: 'Network settings for this unit will appear here once they have been configured.',
          },
          updating_title: 'Updating network settings',
          updating_description: 'Please wait while we fetch the latest network configuration.',
        },
      },
      maintenance: {
        service_protocol_for_chassi: 'Service protocol for chassis {{chassisNumber}}',
        download_pdf: 'Download PDF',
        service: 'Service {{type}}',
        default_service: 'Driveline {{chassisSeries}} {{chassisNumber}}',
        general_comment: 'General service comment',
        no_comment: 'No general comments for this service protocol.',
        periodic_tag: 'Every {{months}} month / {{engineHours}} h',
        non_periodic_month_tag: 'At {{months}} month',
        non_periodic_hours_tag: 'At {{engineHours}} h',
        empty_state: {
          title: 'No maintenance history available',
          message:
            'Service protocols for this engine will appear here once they have been registered by a Volvo Penta Dealer.',
        },
        unverified_ownership_notice:
          'Some information may be limited because ownership of this unit has not yet been verified. Contact your authorized Volvo Penta dealer to complete the verification.',
      },
    },
    settings: {
      application_settings: 'Application settings',
      account_name: 'Fleet account name',
      notifications: 'Notifications',
      email: 'Email',
      email_notifications: 'Enable email notifications',
      sms: 'SMS',
      sms_notifications: 'Enable SMS notifications',
    },
    support: {
      title: 'Frequently asked questions',
      description: 'Find contact information as well as general information about Volvo Penta Connect.',
      professional_support: {
        tag: 'Professional support',
        title: 'The ultimate experts - at your service',
        description:
          'Contact an authorized Volvo Penta dealer as the first port of call when you need help with engine service, repair, parts, or other support - for leisure and commercial vessels.',
      },
      emergency_support: {
        tag: 'Emergency support',
        title: 'Volvo Penta Action Service',
        description:
          'An engine breakdown seldom happens, but if it does, Action Service is there to put you in contact with the Volvo Penta partner closest to you. We offer support in several languages, around the clock, all year round.',
      },
      platform_support: {
        tag: 'Platform support',
        title: 'Volvo Penta Connect Support',
        description:
          'Contact our dedicated team for help with your Volvo Penta Connect account and features. We’re here to help you manage your engine data and connectivity with ease.',
      },
      getting_started: {
        title: 'Getting started',
        question_1: {
          title: 'Who is Volvo Penta Connect for?',
          body: 'Volvo Penta Connect is a customer portal that gives you an overview of your unit(s) and access to relevant information such as manuals and handbooks, campaigns, warranty status, and more.',
        },
        question_2: {
          title: 'What do I need to do first to use this product?',
          body: 'You need to register a unit to your account. Once a unit is added, you can monitor unit data, install over-the-air software updates, and access manuals and handbooks linked to that unit.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Add unit' },
        },
        question_3: {
          title: 'Can I use this product without adding a unit?',
          body: 'No. Volvo Penta Connect is built around your units. Manuals, engine data, and digital services are all linked to a specific unit. If you don’t own or plan to register a unit, this product won’t meet your needs.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Add unit' },
        },
        question_4: {
          title: 'How do I register my engine?',
          body: 'Go to the unit list and select Add unit. Search for the serial number, then complete and submit the registration form. Some newer industrial engines come with a pre-installed QR code on the engine. If available, you can scan this code to start the registration process.',
        },
        question_5: {
          title: 'How do I get started with my unit(s)?',
          body: 'Start by registering your unit in Volvo Penta Connect. Go to the unit list and select Add unit, then search for the unit’s serial number and complete the registration form.<br /><br />Once the unit is registered, you’ll get access to information such as manuals, spare parts, campaigns, warranty status and digital services linked to that unit.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Add unit' },
        },
        question_6: {
          title: 'What can I do once my unit is registered?',
          body: 'With a registered unit, you can:<br /><br /><ul><li>- Group units into fleets by vehicle type or use case</li><li>- Monitor unit status</li><li>- Install over-the-air software updates</li><li>- Access manuals and handbooks specific to your unit</li></ul>',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Unit overview' },
        },
        question_7: {
          title: 'Where can I find manuals & handbooks?',
          body: 'Manuals and handbooks become available once a unit is added to your account, so we can show documentation that matches your specific unit.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Add unit' },
        },
        question_8: {
          title: 'How do I add a user to my fleet account?',
          body: 'To add a new user, you’ll need to invite them from the Members page.<br /><br /><ul><li>1. Go to Members</li><li>2. Select Add member</li><li>3. Create an invitation link and copy it</li><li>4. Share the link with the person you want to invite</li><li>5. Once the invitation is accepted, assign a role to the new member</li></ul><br/>Only users with permission to manage members can send invitations and assign roles.',
          link: { href: 'https://www.volvopenta.com/connect/members', label: 'Members' },
        },
        question_9: {
          title: "Why does my unit get tagged as 'Unverified owner'?",
          body: 'Newly registered units are temporarily marked as Unverified owner until the ownership has been confirmed.<br /><br />Ownership can be verified by an authorized Volvo Penta dealer or by our support team. Once verified, the tag will be removed automatically.<br /><br />If you need help with the verification, please contact your authorized Volvo Penta dealer.',
        },
        question_10: {
          title: "Why can't I see my units?",
          body: "Your units may not appear in your account for several reasons. Please check these common scenarios:<br /><br />Email mismatch:<br/>Your units are linked to a specific email address. If you created an account with a different address, the units won't be visible. You can create a new account with the correct email or contact an authorized Volvo Penta dealer to help re-register your unit.<br /><br />Pending registration:<br/>If you added a new industry unit, it may not be in our records yet. These units won't show in Volvo Penta Connect until our support team completes the manual registration process.<br /><br />Account roles:<br/>Your access to specific units depends on your assigned role. If you believe your access is restricted, please contact your fleet manager to ensure you have the correct account role.",
        },
      },
      connectivity_telematics: {
        title: 'Connectivity & telematics',
        question_1: {
          title: 'How do I know if my unit is connected?',
          body: 'You can see a unit’s connectivity status next to its name in the unit list and on the unit page.<br /><br /><GreenDot /> – The unit is online. Data has been received within the last 6 minutes.<br /><br /><GreyDot /> – The unit is offline. No data has been received in the last 6 minutes.<br /><br />No dot – The unit does not have connectivity capability. It may be eligible for a telematics gateway (TGW) installation through a dealer.<br /><br />If your unit is offline, it can still be used as normal, but data updates will resume once the unit reconnects.',
        },
        question_2: {
          title: 'Why does my unit not show any connectivity status?',
          body: 'Units without a status indicator do not have a telematics gateway (TGW) installed or activated. These units can be connected by installing a TGW through a Volvo Penta dealer.',
        },
        question_3: {
          title: 'What is a telematics gateway (TGW)?',
          body: 'A telematics gateway (TGW) is a hardware device installed on the unit that enables connectivity and data transfer to Volvo Penta Connect.<br /><br />Without a TGW, the unit cannot be monitored digitally.',
        },
        question_4: {
          title: 'How do I connect my unit to Volvo Penta Connect?',
          body: 'To connect a unit, the following steps must be completed:<br /><br /><ul><li>- The unit must have a TGW installed</li><li>- The TGW must be activated by a dealer or OEM</li><li>- Required digital services must be enabled</li><li>- The owner must approve the data management agreement</li></ul><br /><br />Once completed, the unit will appear with connectivity status in Volvo Penta Connect.',
        },
        question_5: {
          title: 'Which smartphones/tablets are compatible with the Volvo Penta Connect app?',
          body: 'For iOS devices, the operating system version must be iOS 17 or later. For Android devices, the operating system version must be 9.0 or later.',
        },
        question_6: {
          title: 'Who can help me install or activate connectivity?',
          body: 'A Volvo Penta dealer can help with TGW installation, activation, and service setup.',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Find a Volvo Penta dealer',
          },
        },
        question_7: {
          title: 'How do I set up Wi-Fi for my unit?',
          body: 'You can configure your Wi-Fi connection directly in the application by following these steps:<br /><br /><ul><li>1. Select the specific unit you wish to configure from your list.</li><li>2. Click on the Settings tab located on the unit page.</li><li>3. Use the Instruction Book (PDF) for step-by-step directions to complete your setup.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/connect/documents/Connect-to-WiFi-onboard.pdf',
            label: 'Instructions manual',
            icon: 'import',
          },
        },
      },
      campaigns_software_updates: {
        title: 'Campaigns & software updates',
        question_1: {
          title: 'What is a campaign?',
          body: 'A campaign is an action initiated by Volvo Penta to ensure product quality. If a campaign affects one of your registered units, you’ll be notified and guided through the required steps.',
          link: { href: '/connect/campaigns', label: 'Campaign overview' },
        },
        question_2: {
          title: 'What do I need to do when I have an active campaign?',
          body: 'It depends on the campaign type:<br /><br /><ul><li>- **Recall / Quality / Service:** Contact your local Volvo Penta dealer</li><li>- **Installation / OTA:** These campaigns can be completed over the air using the Volvo Penta app. Download the app and follow the in-app instructions.</li></ul>',
          link: {
            href: 'https://www.volvopenta.com/about-us/dealer-locator/',
            label: 'Find your dealer',
          },
          ios: { href: '#', label: 'App store' },
          android: { href: '#', label: 'Google play' },
        },
        question_3: {
          title: 'What do I need before installing an over-the-air (OTA) campaign?',
          body: 'Before starting an OTA installation, make sure that:<br /><br /><ul><li>- You’ve selected the correct unit</li><li>- Bluetooth is enabled on your device</li><li>- You’re within 3 meters of the unit</li><li>- All required preconditions shown in the product are met</li><li>- The unit remains powered and undisturbed during installation</li></ul><br/>If any requirement isn’t met, the installation won’t start.',
        },
        question_4: {
          title: 'I’m having trouble installing an over-the-air (OTA) campaign.',
          body: 'If the installation fails or won’t start:<br /><br /><ul><li>- Check that Bluetooth is enabled and you’re close to the unit</li><li>- Stay within 3 meters for the entire installation</li><li>- Restart the app and try again</li><li>- Recheck that all unit preconditions are met</li></ul><br/>If the issue continues, contact support.',
        },
        question_5: {
          title: 'Where can I see my completed over-the-air (OTA) installations?',
          body: 'You can view completed OTA installations on the unit’s page. Open the unit and go to the Campaigns tab to see the full installation history.',
        },
        question_6: {
          title: 'Can poor connectivity affect an OTA installation?',
          body: 'Yes. An OTA installation may fail if the unit has unstable or no cellular or Wi-Fi connectivity. Make sure the unit has a reliable connection and stays connected until the installation is complete.',
        },
        question_7: {
          title: 'Why does my unit need to be regularly connected?',
          body: 'Regular connectivity helps ensure your unit is ready for software updates. If a unit hasn’t been connected for some time, required data or preparations may be missing, which can delay or block an OTA installation.',
        },
        question_8: {
          title: 'What happens if I don’t complete a campaign in time?',
          body: 'Campaigns aren’t installed automatically. If a campaign isn’t completed within the specified timeframe, it may no longer be possible to perform it. Always follow the instructions shown and complete the campaign within the stated period.',
        },
      },
      maintenance_service: {
        title: 'Maintenance & service',
        question_1: {
          title: "Can I see my unit's maintenance history?",
          body: 'Yes. Each unit has a Maintenance history tab where completed services are listed. Only services performed by authorized Volvo Penta dealers are shown. If a service is missing, please contact your dealer directly.<br /><br />Select a unit and open the Maintenance history tab to view the details.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Unit overview' },
        },
        question_2: {
          title: 'Why can’t I see which dealer that performed a service?',
          body: 'Dealer details are only shown to verified owners of the unit.<br /><br />To view the dealer name and location for a completed service, you need to be registered as the owner. If you’re not yet verified, please contact your authorized Volvo Penta dealer to confirm your ownership.',
        },
      },
      warranty_coverage: {
        title: 'Warranty & extended coverage',
        question_1: {
          title: 'How do I know if my unit(s) have any warranty & coverage?',
          body: 'Each unit has a Warranty & coverage tab showing any active warranties. Select a unit and open the tab to see details.',
          link: { href: 'https://www.volvopenta.com/connect/units', label: 'Unit overview' },
        },
        question_2: {
          title: 'What is standard warranty?',
          body: 'All Volvo Penta products include a factory standard warranty. Coverage applies for a defined time period or number of operating hours—whichever comes first—and depends on the product and its application.',
        },
        question_3: {
          title: 'What is the standard unlimited hours warranty?',
          body: 'Some installation types have no operating-hour limit during the first and/or second year of warranty coverage. Details depend on the product and installation and are defined in the warranty terms.',
        },
        question_4: {
          title: 'What is component warranty?',
          body: 'Component warranty covers specific major components of your Volvo Penta product. Coverage duration and operating-hour limits vary by component and are defined in the warranty terms.',
        },
        question_5: {
          title: 'What is emission warranty?',
          body: 'Engines approved under US EPA or California ARB regulations include Emission Warranty coverage. This applies for a defined time period or number of operating hours, as stated in the emission warranty terms.',
        },
      },
      notification: {
        title: 'Notifications',
        question_1: {
          title: 'Why am I not receiving any notifications about my unit(s)?',
          body: 'Check your notification settings. Make sure notifications are enabled on the Settings page.',
          link: { href: 'https://www.volvopenta.com/connect/settings', label: 'Settings' },
        },
      },
    },
    login: {
      hero_title: 'Welcome to Volvo Penta Connect',
      hero_body:
        'Everything regarding your Volvo Penta engine or product in one place. Manuals, handbooks, spare parts, recalls, quality campaigns, warranty status, and more.',
      log_in: 'Log in',
      blurb_back_button: 'Home',
      blurb_bolded_main_text: 'Welcome to Volvo Penta Connect, the new platform for fleet owners and leisure owners.',
      blurb_regular_main_text:
        "Register your engine to access relevant information such as manuals, recalls, and warranty status. Registration ensures your local dealer is ready to support you with the right parts and services. Best of all, it's completely free.",
      CTAs: {
        manuals_label: 'Convenient manual access',
        manuals_title: 'All your manuals in one click',
        manuals_body:
          'With Volvo Penta Connect, you no longer need to search for the serial number to access manuals. Our platform offers a one-click solution, providing you with the full collection of manuals and handbooks for all your engines and products.',
        efficiency_label: 'Efficiency',
        efficiency_title: 'Quickly find the right spare parts',
        efficiency_body:
          'Volvo Penta Connect offers an easy-to-use feature that simplifies the process of finding the correct spare part for your Volvo Penta engine - without the hassle of searching for the serial number or engine model.',
        service_label: 'Track product maintenance',
        service_title: 'View the registered service history for your products',
        service_body:
          'Stay updated on the health of your products with Volvo Penta Connect. Get a comprehensive overview, including service details, of your registered service history for all your Penta products.',
      },
      cards: {
        uptime_label: 'Ensure uptime',
        uptime_title: 'Discover if you have active campaigns',
        uptime_body:
          'With Volvo Penta Connect, keeping track of your product campaigns has never been easier. Ensure your products are up-to-date and always functioning at their best.',
        connect_label: 'Keeping track of all your warranties',
        connect_title: 'Your warranties in Volvo Penta Connect',
        connect_body:
          'Get an overview of all your warranties and coverages. Check whether they are active and how long they will be valid, and receive notifications when they are about to expire.',
      },
    },
    welcome: {
      title: 'Welcome!',
      body: 'To fully utilize Volvo Penta Connect you need to register a vessel or machine to your account. Once added you will have access to manuals and handbooks, easier access to relevant spare parts, warranty statuses, and much more.',
      get_help: 'Get help',
      get_started: 'Get started',
      accordion: {
        who_is_it_for: {
          title: 'Who is Volvo Penta Connect for?',
          body: 'Volvo Penta Connect is a customer portal where you get an overview of your unit(s) and relevant information such as manuals & handbook, campaigns, warranty status and more.',
        },
        how_to_register: {
          title: 'Can I use this product without registering a unit?',
          body: 'You need to register a unit to your account. Once a unit is added, you can monitor unit data, install over-the-air software updates, and access manuals and handbooks linked to that unit.',
        },
        can_i_use_without_registering: {
          title: 'Where can I find manuals & handbooks?',
          body: 'Manuals and handbooks become available once a unit is added to your account, so we can show documentation that matches your specific unit.',
        },
      },
    },
  },
  components: {
    modal: {
      accept_invitation_modal: {
        invite_title: 'Welcome to {{name}}!',
        invite_description:
          'You have been invited to the {{fleetName}} workspace. Here you can monitor fleet and unit health, keep up with analytics, as well as get notifications on campaign and warranty updates.',
        accept: 'Accept invite',
        dismiss: 'Dismiss',
      },
      add_units_fleet_modal: {
        button_add: 'Create fleet',
        button_update: 'Update fleet',
        title_add: 'Create fleet',
        title_success: 'Fleet created successfully',
        title_update: 'Update fleet',
        fleet_name: 'Enter fleet name',
        select_your_fleet: 'Select your fleet',
        click_to_create_fleet: 'Click to create fleet',
        table: {
          unit_name: 'Unit name',
          location: 'Location',
          engines: 'Engines',
          fleets: 'Fleets',
        },
        text_success:
          'Your fleet has been created. If the new fleet does not appear in the list, please refresh the page.',
        cancel: 'Skip for now',
        submit: {
          add_units: 'Add unit(s) to fleet',
          create_fleet: 'Create fleet',
          close: 'Close',
        },
      },
      remove_units_fleet_modal: {
        button_remove: 'Remove from fleet',
        title_remove: 'Remove units from fleet',
        description: 'You are about to remove the selected units from the fleet. This action cannot be undone.',
        confirmation: 'Are you sure you want to remove the selected units from the fleet?',
        cancel: 'Cancel',
        submit: {
          create_fleet: 'Remove from fleet',
        },
      },
      delete_fleet_modal: {
        title: 'Remove fleet',
        description:
          'Removing a fleet from your account is done permanently. You have the option to re-add a fleet at any time.',
        confirmation: 'Are you sure you want to remove the following fleet?',
        submit: 'Yes, remove fleet from account',
      },
      edit_fleet_modal: {
        title: 'Edit fleet',
        fleet_name: 'Fleet name',
        select_icon: 'Select icon',
      },
      add_new_unit_modal: {
        button: 'Add unit',
        search_unit: {
          title: 'Find your unit',
          description: 'Search the unit you want to add using the engine serial number.',
          serial_number: 'Engine serial number',
          find_serial_number: 'How to find your serial number',
          is_not_found: {
            label: 'Serial number does not exist in our records',
            body: 'Please make sure you found the right serial number. If it still doesn’t work, please contact your Volvo Penta dealer or find one in our <a>Dealer Locator</a> to get help.',
          },
          is_not_registered_industrial: {
            label: 'Unit not registered for this product',
            body: 'Register the unit in order to get it added to your account. The registration request will be handled by our support team and the unit will be added after completion.',
          },
          is_not_registered_marine: {
            label: 'Unit not registered for this product',
            body: 'The unit need to be registered in order for you to add it to your account. Please contact your Volvo Penta dealer or find one in our <a>Dealer Locator</a> to get help with unit registration.',
          },
          is_maximum_units_reached: {
            label: 'Maximum number of units reached',
            body: 'You can add up to three non-owned units. To add more, you need to be registered as the owner of the unit. Please contact your Volvo Penta dealer for help, or use our Dealer Locator to find one near you.',
          },
          is_unit_already_added: {
            label: 'Unit already added to your account',
            body: 'This unit is already available in your account and does not need to be registered again. You can find it in your unit list.',
          },
        },
        unit_information: {
          title: 'Enter unit information',
          description:
            'Please fill in the details below to add your vessel to Volvo Penta Connect and receive relevant information about your Penta Product.',
          privacy_information:
            'Volvo Penta registers and uses certain personal data about you. If you are interested in learning more about how Volvo Penta process your personal data, please go to <a>Privacy information for registration of owner or operator</a>.',
        },
        marine_unit_information: {
          serial_number: 'Engine serial number',
          serial_number_helperText: 'The Volvo Penta serial number is found on the engine plate.',
          vessel_name: 'Vessel name',
          vessel_name_helperText: 'The name (eg. Black Pearl) given to your vessel.',
          vessel_id: 'Vessel ID',
          imo_number: 'IMO number',
          imo_number_helperText: 'International Maritime Organization number uniquely identifying your vessel.',
          mmsi_number: 'MMSI number',
          mmsi_number_helperText: 'Maritime Mobile Service Identity number used for radio communication.',
          manufacturer: 'Manufacturer',
          manufacturer_helperText: 'The manufacturer of your vessel.',
          model: 'Model',
          model_helperText: 'The model of the vessel.',
          vessel_segment: 'Vessel segment',
          type_of_vessel: 'Type of vessel',
          home_port: 'Home port',
          home_port_helperText: 'Site or home port of your vessel',
          add_unit: 'Add unit',
        },
        marine_unit_added: {
          title: 'Your unit has been added',
          description:
            'Your vessel is added and you will be able to see your vessels information, download manuals, find spare parts, and more.',
          add_another_unit: 'Add another unit',
          go_to_unit: 'Go to unit',
        },
        industrial_contact_information: {
          title: 'Enter contact information',
          description:
            'Please fill in the owners info details below to add your unit. Your entered details will be controlled manually by our support team before registering you as an owner.',
          proof_of_ownership: 'Proof of ownership',
          proof_of_ownership_helperText:
            'A proof of ownership (eg. contract, receipt or order confirmation) is required to register the company as an owner of the machine. If you can not provide ownership, please contact your dealer.',
          proof_of_ownership_upload:
            'Drag and drop or click to browse files, PDF, png, jpg, or heic files of max 3mb each.',
          proof_of_ownership_qr: 'Ownership confirmed by QR code',
          contact_person: 'Contact person',
          contact_person_description: 'You will be registered as the contact person for the company and this machine.',
          first_name: 'First name',
          last_name: 'Last name',
          email: 'Email',
          company_details: 'Company details',
          company_details_description: 'Enter the information of the company owning the machine.',
          company_name: 'Company name',
          company_name_helperText: 'The company owning the machine.',
          vat_number: 'VAT number',
          address_line: 'Address',
          zip_code: 'Postal / Zip code',
          city: 'City',
          state: 'State / Province',
          country: 'Country',
          notification: 'Notification preferences',
        },
        industrial_unit_information: {
          engine_serial_number: 'Engine serial number',
          engine_serial_number_helperText:
            "If your machine has more than one engine, click to add another field and enter the additional engine's serial number.",
          add_engine: 'Add engine',
          machine_name: 'Machine name',
          machine_name_helperText: 'The name (eg. T4, Unit 11) of your machine, vehicle or power generator.',
          machine_id: 'Machine ID',
          machine_id_helperText:
            'The unique identification number provided by the original equipment manufacturer (OEM).',
          manufacturer: 'Manufacturer',
          manufacturer_helperText:
            'The manufacturer of your machine, vehicle or power generator (e.g. Magni, Inmesol, Atlas).',
          model: 'Model',
          model_helperText: 'The model of your machine, vehicle or power generator.',
          machine_segment: 'Machine segment',
          machine_segment_helperText: 'The segment your machine is in (eg. Material handling, mining, forestry).',
          type_of_machine: 'Type of Machine',
          type_of_machine_helperText: 'What type of machine it is (eg. terminal tractor, harvester, forklift).',
          operating_location: 'Operating location',
          operating_location_helperText: 'Site or operating location of your machine.',
        },
        industrial_unit_added: {
          title: 'Unit added',
          description:
            'Your machine is added and you will be able to see your machine information, download manuals, find spare parts, and more.',
          manual_ownership_registration: 'Manual ownership registration',
          manual_ownership_registration_description:
            'Registration and ownership control are done manually by our support team. <b>Currently we are facing high demand, which leads to long processing times, expect between 2-6 weeks.</b> You will receive a confirmation email when your request is approved.',
          add_another_unit: 'Add another unit',
          go_to_unit: 'Go to unit',
        },
        industrial_no_unit_added: {
          title: 'Machine won’t show in Volvo Penta Connect',
          description:
            'Your machine does not exist in our records. It will not show in Volvo Penta Connect until the manual registration process by our support team is completed.',
          manual_registration: 'Manual registration',
          manual_registration_description:
            'Registration and ownership control are done manually by our support team. <b>Currently we are facing high demand, which leads to long processing times, expect between 2-6 weeks.</b> You will receive a confirmation email when your request is approved.',
          manuals_campaigns: 'Manuals, parts and campaigns',
          manuals_campaigns_description:
            'If you’re looking for manuals and handbooks, spare parts or campaigns - here are links where you can get it until the registration is completed. You have also received this information in your email inbox.',
          add_another_unit: 'Add another unit',
        },
      },
      invite_members_modal: {
        button: 'Add members',
        title: 'Pending invitation links',
        empty_state: {
          title: 'Did not find any invites.',
          message: 'Try to create and copy an invite to your clipboard with the button below.',
        },
        generate_invitation_link: 'Generate and copy new invitation link',
      },
      remove_members_modal: {
        title: 'Remove member(s)',
        description:
          'Removing a member from the {{count}} account is done permanently. You have the option to re-invite a member at any time.',
        are_you_sure: 'Are you sure you want to remove the following member(s)?',
        cancel: 'Cancel',
        submit: 'Yes, remove member(s) from account',
      },
      add_network_modal: {
        button: 'Add Network',
        title: 'Add Network',
        refresh_network: 'Refresh network',
        edit_network: 'Edit network',
        remove_network: 'Remove network',
        remove_network_description:
          'Remove network from your saved list for this vehicle / vessel. Removing the network is done permanently. You can always add the network again.',
        network_name: 'Network name (SSID)',
        network_name_helperText: 'Network id of the wifi router',
        password: 'Password',
        password_helperText: 'Password to the wifi router',
        error_title: 'Something went wrong',
        error_description: 'Unable to update the network. Please try again later.',
        priority: 'Priority',
      },
      wifi_polling_modal: {
        title: 'Updating Network Settings',
        title_error: 'Network Settings Update',
        connecting: 'Connecting to your device...',
        searching: 'Searching for your network settings...',
        almost: 'Almost there, fetching configuration...',
        hang_tight: 'Still working on it, hang tight...',
        communicating: 'Communicating with the gateway...',
        moment: 'Just a moment longer...',
        timeout: 'Unable to reach your device. Please try again later.',
        error: 'Something went wrong while updating network settings. Please try again.',
      },
      wifi_error_log_modal: {
        title: 'Error log',
        no_errors: 'No errors found',
        close: 'Close',
      },
      digital_services_modal: {
        activate_service_content: {
          title: 'Activate service',
          description:
            'To help us prepare the correct activation contract and request the right contact details, please specify on whose behalf you are activating this service.',
          myself: 'Myself',
          myself_helper: 'Activate this service for your personal machine or vessel.',
          company: 'A company',
          company_helper: 'Activate this service on behalf of a business, fleet, or organization.',
        },
        contract_details_content: {
          title: 'Contact details',
          first_name: 'First name',
          last_name: 'Last name',
          email_address: 'Email address',
          company_name: 'Company Name',
          company_details_title: 'Company details',
          company_details_description: 'Enter the information of the company owning the machine.',
          vat_number: 'VAT number',
          social_security_number: 'Social security number',
          state_province: 'State/Province',
          country: 'Country',
          address: 'Address',
          city: 'City',
          postal_code: 'Postal Code',
          is_correct_info: 'I hereby confirm that the information provided above is true and correct.',
          is_terms_checked:
            'I acknowledge and confirm that I have read and understood the terms and conditions of the Digital Services Agreement (including appendices) and that I am authorized to accept said <a>contract</a> with legally binding effect.',
          data_management_agreement: 'I have also read and understood the <a>Data Management Agreement</a>.',
          remote_software_updates_title: 'Remote Software Updates',
          service_description_for: 'Service Description for {{serviceName}}',
          is_service_details_checked:
            'I acknowledge and confirm that I have read and understood the <serviceDescription /> and that I am authorized to accept said service details.',
          is_data_act_checked:
            'The EU Data Act requires that we provide you with pre-contractual information describing how a Volvo Penta Connected Product (placed on the market in an EU member state) generates product data, how the product data is collected, how you as a user can access it, and how it is used by Volvo Penta. The complete information is available at <a>EU Data Act | Volvo Penta</a>. By checking this box and proceeding with the purchase, lease, or rental of the Volvo Penta Connected Product, you acknowledge that you have been provided with access to this precontractual information as required under the EU Data Act.',
        },
        choose_receiver_content: {
          title: 'Choose a dealer',
          description:
            'Please add a dealer to receive the alert notifications. This ensures maximum uptime and most efficient service planning for your Volvo Penta product.',
          find_nearest_dealer: 'Find your nearest dealer',
          dealer: 'Dealer',
          dealer_placeholder: 'Enter the dealer name and/or their email address',
        },
        thanks_content: {
          title: 'Thank you!',
          description:
            'Please note that activation is not immediate and will take some time. We will notify you by email when the service is ready to use.',
          description_all_services: 'We will notify you by email once services are ready to use.',
          Marine: {
            description:
              'Please note that activation is not immediate and might take some time. We will notify you by email once the activation is completed.',
          },
        },
        deactivate_service: {
          title: 'Terminate service',
          description: 'Are you sure that you want to terminate the service?',
          description_all: 'Are you sure that you want to terminate all services, ({{serviceNames}})?',
          deactivate: 'Terminate',
        },
      },
    },
    table: {
      items_selected: '{{selectedRowsCount}} items selected',
      page: 'Page {{currentPage}} of {{totalPages}}',
      unit_table: {
        unit_name: 'Unit name',
        model_brand: 'Model/brand',
        location: 'Location',
        engines: 'Engines',
        operating_hours: 'Operating hours',
        fleets: 'Fleets',
        campaigns: 'Campaigns',
        digital_services: 'Digital Services',
        unverified_ownership: 'Unverified ownership',
      },
      campaigns_table: {
        campaign: 'Campaign',
        download: 'Download',
        created_date: 'Created date',
        valid_to: 'Valid to',
        product: 'Product',
        type: 'Type',
      },
      members_table: {
        name: 'Name',
        email: 'Email',
        account: 'Account',
      },
      invitations_table: {
        creation_date: 'Creation date',
      },
      specifikationer_table: {
        driveline: 'Driveline',
        product_type: 'Product type',
        product: 'Product',
        serial_number: 'Serial number',
        operating_hours: 'Operating hours',
        engine: 'Engine',
        segment: 'Segment',
        rated_power: 'Rated Power',
        rated_speed: 'Rated Speed',
      },
      network_settings_table: {
        priority: 'Priority',
        ssid: 'SSID',
        frequency: 'Frequency',
        last_updated: 'Last updated',
        connection: 'Connection',
        connected: 'Connected',
        not_connected: 'Not connected',
        lost_connection: 'Lost connection',
        disconnected: 'Disconnected',
      },
      coverage_table: {
        type: 'Type',
        description: 'Warranty/Coverage',
        expiration_hours: 'Expiration hours',
        expiration_date: 'Expiration date',
      },
      fault_codes_table: {
        unit: 'Unit',
        fault_code: 'Fault code',
        fault_description: 'Description',
        failure_event: 'Failure Event',
        timestamp: 'Timestamp',
        operating_hours: 'Operating hours',
        occurrences: 'Occurrences',
        severity: 'Severity',
      },
      connectivity_table: {
        unit: 'Unit',
        operating_hours: 'Operating hours',
        last_seen: 'Last seen',
        status: 'Connectivity status',
      },
      fuel_consumption_table: {
        unit: 'Unit',
        chassis_id: 'Chassis ID',
        operating_hours: 'Operating hours (h)',
        fuel_efficiency: 'Fuel efficiency (l/h)',
        total_fuel: 'Total fuel (l)',
      },
      publications_table: {
        title: 'Title',
        description: 'Description',
        designation: 'Product',
        product: 'Product type',
        size: 'Size',
        languageCode: 'Lang',
        buy: 'Buy',
      },
      maintenance_dealer_table: {
        header: 'Dealership',
        dealer_name: 'Dealer name',
        location: 'Location',
      },
      maintenance_protocol_details_table: {
        header: 'Service protocol details',
        title: 'Title',
        current_service: 'Current service',
        previous_service: 'Previous service',
        date: 'Date',
        operating_hours: 'Operating Hhurs',
        unit_age: 'Unit age',
        months: 'months',
      },
      maintenance_driveline_table: {
        header: 'Driveline {{driveline}}',
        name: 'Name',
        serial_number: 'Serial number',
        product: 'Product',
      },
      maintenance_service_table: {
        header: 'Service {{service}}',
        activity: 'Activity',
        detail: 'Detail',
        last_performed: 'Last performed',
        performed: 'Performed',
        complete: 'Complete',
        not_performed: 'Not performed',
      },
    },
    tooltip: {
      copy_clipboard: 'Copy to clipboard',
    },
    language_selector: {
      de: 'Sprache - Deutsch',
      en: 'Language - English',
      fr: 'Langue - Français',
      it: 'Lingua - Italiano',
      nl: 'Taal - Nederlands',
      pt: 'Idioma - Português',
      sv: 'Språk - Svenska',
    },
    main_menu: {
      home: 'Home',
      settings: 'Settings',
      support: 'Support',
    },
    user_menu: {
      title: 'User menu',
      account_settings: 'Account settings',
      logout: 'Log out',
      switch_fleet_account: 'Switch fleet account',
    },
    footer: {
      useful: 'Useful',
      manuals_and_handbooks: 'Manuals & handbooks',
      dealer_locator: 'Dealer Locator',
      warranty: 'Warranty',
      parts_column: 'Parts and Accessories',
      parts: 'Parts',
      remanufactured: 'Remanufactured components and engines',
      contact: 'Contact',
      action_service: 'Volvo Penta Action Service',
      faq: 'FAQ',
      contact_us: 'Contact us',
      privacy_policy: 'Privacy Policy',
      cookie_policy: 'Cookie Policy',
    },
    cta: {
      members_fleetaccount_cta: {
        heading: 'Invite others to your account',
        message:
          'Add members to share access to your units and fleets. You can invite colleagues, business partners, or family members and assign roles to control what they can see and manage. Copy the link and send to your member.',
      },
      members_digitalaccount_cta: {
        heading: 'Invite others to your account',
        message:
          'Inviting members requires a verified unit owner. Once your ownership has been confirmed, you’ll be able to invite colleagues, business partners, or family members and assign roles to control what they can see and manage.<br /><br />To verify ownership, please contact your authorized Volvo Penta dealer.',
      },
      action_cta: {
        heading: 'How to take action',
        message: 'To perform a campaign, contact your Volvo Penta or OEM dealer.',
        find_dealer: 'Find your nearest dealer',
        learn_more: 'Learn more',
      },
      notifications_cta: {
        heading: 'Notifications',
        message: 'Stay updated and get notified by email when a unit have an installable campaign available. ',
        notification_settings: 'Notification settings',
      },
    },
    role_types: {
      title: 'Role information',
      manager: {
        title: 'Manager',
        description:
          'Managers have full administrative control to oversee the fleet, monitor unit health, and manage all units, settings, and user access.',
      },
      technician: {
        title: 'Technician',
        description:
          'Focuses on unit health and maintenance. Technicians can monitor unit data and perform remote software updates. This role does not include fleet or user management permissions.',
      },
      viewer: {
        title: 'Viewer',
        description:
          'Viewers have read-only access. They can monitor unit status and view reports, but cannot change settings or configurations.',
      },
    },
    campaign_types: {
      title: 'Understanding campaign types',
      recall: {
        title: 'Recall',
        description:
          'Retroactive introduction of production change; should be implemented at the earliest possible convenience in order to secure proper operation of the product.',
      },
      quality: {
        title: 'Quality',
        description:
          'Retroactive introduction of production change; should be implemented at next scheduled service in order to secure proper operation of the product.',
      },
      service: {
        title: 'Service',
        description:
          'Safety related retroactive introduction of production change; must be implemented immediately in order to secure safe operation of the product.',
      },
      installation: {
        title: 'Installation',
        description:
          'A remote software update is ready for you to install on your unit using the Volvo Penta Application.',
        app_store: 'Get it for iOS on App store',
        google_play: 'Get it for Android on Google Play',
      },
    },
  },
};
